import Vue from 'vue';
import store from '../store';
import Mutations from '../mutations';
// models
import ChatMessage from '@/models/ChatMessage';
import Connection from '@/models/Connection';
import DateOptions from '@/models/DateOptions';
import Node from '@/models/Node';
import { PacketType } from '@/models/Packet';
import { Permissions } from '@/models/PermissionEnum';
import Project from '@/models/Project';
import StringPacking from '@/models/StringPacking';
import Tag from '@/models/Tag';
import Timeline from '@/models/Timeline';
import TimelineAccess, { TimelinePermissions } from '@/models/TimelineAccess';
import User from '@/models/User';
import UserGroup from '@/models/UserGroup';
// utilities
import { SendPacket } from '@/networking/SendPacket';
import { IsEmbedBuild } from '@/util/embed_schema';
import router from '@/router';
import { ClientSubscription, ClientSubscriptionStatus } from '@/models/payment/PaymentMethods';
import { DeleteFolderData, SetFolderData, TimelineSetFolderData } from '@/models/packets/Folder';
import { FolderUpdateAccessData } from '@/models/packets/Folder';
import Folder, { hasFolderPermission } from '@/models/Folder';

function isCurrentlyEmbedded() 
{
    return IsEmbedBuild() || router.currentRoute.fullPath.includes('/app/e/');
}

require('@/models/ArrayExtension');

function getDefaultState() 
{
    return {
        project: new Project(),
        nodes: new Map<number, Node>(),
        mapNodes: new Map<number, Node>(),
        timelines: new Map<number, Timeline>(),
        mapToTimeline: new Map<number, number[]>(),
        globalTags: new Map<number, Tag>(),
        connections: new Map<number, Map<number, Connection>>(), // in lieu of a number pair for a key, using a Map of Maps; still O(1) lookup
        chatChannels: new Map<number, ChatMessage[]>(),
        folders: new Map<number, Folder>(),

        nodesTracker: false,
        mapNodesTracker: false,
        timelinesTracker: false,
        mapToTimelineTracker: false,
        globalTagsTracker: false,
        connectionsTracker: false,
        chatTracker: false,
        folderTracker: false,

        activeTimeline: <Timeline><unknown>null, // TODO: proper way to do this is <Timeline | null>null
        selected: <any><unknown>null, // TODO: proper way to do this is <any | null>null
        lastSelected: <any><unknown>null, // TODO: proper way to do this is <any | null>null
        presentationMode: false,
        presentationFullSlide: -1,
        timelineFormMode: false,
        copyingTimelines: <number[]>[],
        accessedTimelines: <number[]>[],
        timelineSyncing: false,
    };
}

const state = getDefaultState();
const typedState = state;

const getters =
{
    // *Tracker booleans are a workaround to create reactive references to each Map, since reactive Maps won't be a thing until Vue 3.0 releasing Q3 2019
    nodes: (state: typeof typedState) => 
    {
        return state.nodesTracker ? state.nodes : state.nodes;
    },
    folders: (state: typeof typedState) => 
    {
        return state.folderTracker ? state.folders : state.folders;
    },
    activeTimelineNodes: (state: typeof typedState, getters: any) => 
    {
        const tl = state.activeTimeline;
        if (tl != null) 
        {
            const tlnodes = [];
            for (const nodeid of tl.nodeIDs) 
            {
                //console.log(nodeid);
                tlnodes.push(getters.nodes.get(nodeid));
            }
            return tlnodes;
        }
        return null;
    },
    mapNodes: (state: typeof typedState) => 
    {
        return state.mapNodesTracker ? state.mapNodes : state.mapNodes;
    },
    timelines: (state: typeof typedState) => 
    {
        return state.timelinesTracker ? state.timelines : state.timelines;
    },
    mapToTimeline: (state: typeof typedState) => 
    {
        return state.mapToTimelineTracker ? state.mapToTimeline : state.mapToTimeline;
    },
    globalTags: (state: typeof typedState) => 
    {
        return state.globalTagsTracker ? state.globalTags : state.globalTags;
    },
    connections: (state: typeof typedState) => 
    {
        return state.connectionsTracker ? state.connections : state.connections;
    },
    activeTimelineConnections: (state: typeof typedState, getters: any) => 
    {
        const tl = state.activeTimeline;
        if (tl != null) 
        {
            let connections: Connection[] = [];
            for (const key of getters.connections.keys()) 
            {
                if (tl.nodeIDs.includes(key)) 
                {
                    connections = connections.concat([...getters.connections.get(key).values()]); // NOTE: just assuming that node2 is in the timeline for now
                }
            }
            //console.log(connections);
            return connections;
        }
        return null;
    },
    chatChannels: (state: typeof typedState) => 
    {
        return state.chatTracker ? state.chatChannels : state.chatChannels;
    },
    presentationMode: (state: typeof typedState) => 
    {
        return state.presentationMode;
    },
    timelineFormMode: (state: typeof typedState) => 
    {
        return state.timelineFormMode;
    },
    accessedTimelines: (state: typeof typedState) => 
    {
        return state.accessedTimelines;
    },
    timelineSyncing: (state: typeof typedState) => 
    {
        return state.timelineSyncing;
    },
    checkTimelineCopying: (state: typeof typedState) => 
    {
        return (timelineid?: number) => 
        {
            if (timelineid == undefined) 
            {
                timelineid = state.activeTimeline.id;
            }
            return state.copyingTimelines.includes(timelineid);
        };
    },
    checkAdmin: (state: typeof typedState) => 
    {
        return (group?: UserGroup) => 
        {
            if (isCurrentlyEmbedded()) return false;
            if (group == null) 
            {
                group = state.project.group;
            }
            return group != null && group.id == 0;
        };
    },
    checkProjectDelete: (state: typeof typedState) => 
    {
        return (group?: UserGroup) => 
        {
            if (isCurrentlyEmbedded()) return false;
            if (group == null) 
            {
                group = state.project.group;
            }
            return group != null && (Permissions.check(group.permissions, Permissions.ProjectDelete));
        };
    },
    checkProjectTimelineForceWrite: (state: typeof typedState) => 
    {
        return (group?: UserGroup) => 
        {
            if (isCurrentlyEmbedded()) return false;
            if (group == null) 
            {
                group = state.project.group;
            }
            return group != null && (Permissions.check(group.permissions, Permissions.TimelineForceWrite));
        };
    },
    checkInviteManage: (state: typeof typedState) => 
    {
        return (group?: UserGroup, target?: UserGroup) => 
        {
            if (isCurrentlyEmbedded()) return false;
            if (group == null) 
            {
                group = state.project.group;
            }
            return group != null && (Permissions.check(group.permissions, Permissions.InviteManage))
                && (target == null || getters.checkPowerLevelGreaterOrEqual(state)(group, target));
        };
    },
    checkUserManage: (state: typeof typedState) => 
    {
        return (group?: UserGroup, target?: UserGroup) => 
        {
            if (isCurrentlyEmbedded()) return false;
            if (group == null) 
            {
                group = state.project.group;
            }
            return group != null && (Permissions.check(group.permissions, Permissions.UserManage))
                && (target == null || getters.checkPowerLevelGreaterOrEqual(state)(group, target));
        };
    },
    checkProjectModerate: (state: typeof typedState) => 
    {
        return (group?: UserGroup) => 
        {
            if (isCurrentlyEmbedded()) return false;
            if (group == null) 
            {
                group = state.project.group;
            }
            return group != null && (Permissions.check(group.permissions, Permissions.ProjectModerate));
        };
    },
    checkUserGroupEdit: (state: typeof typedState) => 
    {
        return (group?: UserGroup, target?: UserGroup) => 
        {
            if (isCurrentlyEmbedded()) return false;
            if (group == null) 
            {
                group = state.project.group;
            }
            return group != null && (Permissions.check(group.permissions, Permissions.UserGroupEdit))
                && (target == null || getters.checkPowerLevelGreater(state)(group, target));
        };
    },
    checkMapEdit: (state: typeof typedState) => 
    {
        return (group?: UserGroup) => 
        {
            if (isCurrentlyEmbedded()) return false;
            if (group == null) 
            {
                group = state.project.group;
            }
            return group != null && (Permissions.check(group.permissions, Permissions.MapEdit));
        };
    },
    checkMetadataEdit: (state: typeof typedState) => 
    {
        return (group?: UserGroup) => 
        {
            if (isCurrentlyEmbedded()) return false;
            if (group == null) 
            {
                group = state.project.group;
            }
            return group != null && (Permissions.check(group.permissions, Permissions.MetadataEdit));
        };
    },
    checkFolderEdit: (state: typeof typedState) => 
    {
        // Currently the same as checkMetadataEdit, but separated this out so that
        // we can easily refactor it into its own permission later if we need.
        return (group?: UserGroup) => 
        {
            if (isCurrentlyEmbedded()) return false;
            if (group == null) 
            {
                group = state.project.group;
            }
            return group != null && (Permissions.check(group.permissions, Permissions.MetadataEdit));
        };
    },
    checkProjectAccess: (state: typeof typedState) => 
    {
        return (group?: UserGroup) => 
        {
            if (isCurrentlyEmbedded()) return true;
            if (group == null) 
            {
                group = state.project.group;
            }
            return group != null && Permissions.check(group.permissions, Permissions.ProjectAccess);
        };
    },
    checkTimelineForceRead: (state: typeof typedState, getters: any) => 
    {
        if (isCurrentlyEmbedded()) return true;
        return getters.checkTimelineForceShare || (state.project.group != null && Permissions.check(state.project.group.permissions, Permissions.TimelineForceRead));
    },
    checkTimelineForceShare: (state: typeof typedState, getters: any) => 
    {
        if (isCurrentlyEmbedded()) return false;
        return getters.checkTimelineForceWrite || (state.project.group != null && Permissions.check(state.project.group.permissions, Permissions.TimelineForceShare));
    },
    checkTimelineForceWrite: (state: typeof typedState, getters: any) => 
    {
        if (isCurrentlyEmbedded()) return false;
        return getters.checkTimelineProjectModerate || (state.project.group != null && Permissions.check(state.project.group.permissions, Permissions.TimelineForceWrite));
    },
    checkTimelineProjectModerate: (state: typeof typedState) => 
    {
        if (isCurrentlyEmbedded()) return false;
        return state.project.group != null && Permissions.check(state.project.group.permissions, Permissions.ProjectModerate);
    },
    checkTimelineAdmin: (state: typeof typedState, getters: any, rootState: { userdata: User }) => 
    {
        return (timelineid: number = -1) => 
        {
            if (isCurrentlyEmbedded()) return false;
            let timeline = state.timelines.get(timelineid);
            if (timeline == null) 
            {
                timeline = state.activeTimeline;
            }
            return getters.checkAdmin() || (timeline != null && timeline.hasPermission(TimelinePermissions.Admin, rootState.userdata.userid.toLowerCase()));
        };
    },
    checkFolderAdmin: (state: typeof typedState, getters: any, rootState: { userdata: User }) =>
    {
        return (folderid: number = -1) =>
        {
            if (isCurrentlyEmbedded()) return false;
            const folder = state.folders.get(folderid);
            if (folder == null)
            {
                throw "Folder not found";
            }
            return getters.checkAdmin() || (folder != null && hasFolderPermission(folder, rootState.userdata.userid.toLowerCase(), TimelinePermissions.Admin));
        };
    },
    checkTimelineShare: (state: typeof typedState, getters: { checkTimelineForceShare: any, checkAdmin: any }, rootState: { userdata: User }) => 
    {
        return (timelineid: number = -1) => 
        {
            if (isCurrentlyEmbedded()) return false;
            let timeline = state.timelines.get(timelineid);
            if (timeline == null) 
            {
                timeline = state.activeTimeline;
            }
            
            const hasPerm = getters.checkAdmin()
                || getters.checkTimelineForceShare
                || (timeline != null && timeline.hasPermission(TimelinePermissions.Share, rootState.userdata.userid.toLowerCase()));
            if (!hasPerm && timeline.folderid)
            {
                // Check folder perm
                const folder = state.folders.get(timeline.folderid);
                if (folder != null)
                {
                    const access = folder.accessList.find(x => x.User.userid.toLowerCase() == rootState.userdata.userid.toLowerCase());
                    if (access != null)
                    {
                        return TimelinePermissions.check(access.Permission, TimelinePermissions.Share);
                    }
                }
            }
            return hasPerm;
        };
    },
    checkTimelineWrite: (state: typeof typedState, getters: { checkAdmin: any, checkWrite: any, checkTimelineForceWrite: any }, rootState: { userdata: User }) => 
    {
        return (timelineid: number = -1) => 
        {
            if (isCurrentlyEmbedded()) return false;
            let timeline = state.timelines.get(timelineid);
            if (timeline == null)
            {
                timeline = state.activeTimeline;
            }
            
            const hasPerm = getters.checkAdmin()
                || getters.checkTimelineForceWrite
                || (timeline != null && timeline.hasPermission(TimelinePermissions.Write, rootState.userdata.userid.toLowerCase()));
            if (!hasPerm && timeline.folderid)
            {
                console.log("Checking folder perm");
                // Check folder perm
                const folder = state.folders.get(timeline.folderid);
                if (folder != null)
                {
                    console.log("Folder found, checking access...");
                    console.log({timeline, folder});
                    const access = folder.accessList.find(x => x.User.userid.toLowerCase() == rootState.userdata.userid.toLowerCase());
                    if (access != null)
                    {
                        return TimelinePermissions.check(access.Permission, TimelinePermissions.Write);
                    }
                    else
                    {
                        console.log("Access not found...?");
                    }
                }
                else
                {
                    console.log("Folder not found...?");
                }
            }
            return hasPerm;
        };
    },
    checkTimelineRead: (state: typeof typedState, getters: { checkProjectAccess: any, checkAdmin: any, checkTimelineForceRead: any, checkTimelineForceWrite: any }, rootState: { userdata: User }) => 
    {
        return (timelineid: number = -1) => 
        {
            if (isCurrentlyEmbedded()) return true;
            let timeline = state.timelines.get(timelineid);
            if (timeline == null) 
            {
                timeline = state.activeTimeline;
            }
            
            const hasPerm = getters.checkAdmin()
                || getters.checkTimelineForceRead
                || (getters.checkProjectAccess() && (timeline != null && timeline.hasPermission(TimelinePermissions.Read, rootState.userdata.userid.toLowerCase())));
            if (!hasPerm && timeline.folderid)
            {
                // Check folder perm
                const folder = state.folders.get(timeline.folderid);
                if (folder != null)
                {
                    const access = folder.accessList.find(x => x.User.userid.toLowerCase() == rootState.userdata.userid.toLowerCase());
                    if (access != null)
                    {
                        return TimelinePermissions.check(access.Permission, TimelinePermissions.Read);
                    }
                }
            }
            return hasPerm;
        };
    },
    checkPowerLevelGreaterOrEqual: (state: typeof typedState) => 
    {
        return (group?: UserGroup, target?: UserGroup) => 
        {
            if (isCurrentlyEmbedded()) return false;
            if (target == null) 
            {
                return false;
            }
            if (group == null) 
            {
                group = state.project.group;
            }
            return group != null && group.powerlevel >= target.powerlevel;
        };
    },
    checkPowerLevelGreater: (state: typeof typedState) => 
    {
        return (group?: UserGroup, target?: UserGroup) => 
        {
            if (isCurrentlyEmbedded()) return false;
            if (target == null) 
            {
                return false;
            }
            if (group == null) 
            {
                group = state.project.group;
            }
            return group != null && group.powerlevel > target.powerlevel;
        };
    },

    getConnection: (state: typeof typedState) => 
    {
        return (id1: number, id2: number) => 
        {
            if (id1 > id2) 
            {
                const t = id2;
                id2 = id1;
                id1 = t;
            }
            const cm = state.connections.get(id1);
            if (cm != null) 
            {
                return cm.get(id2);
            }
            return undefined;
        };
    },
};

const typedGetters = getters;

const mutations =
{
    [Mutations.chatMessage](state: typeof typedState, data: ChatMessage): void 
    {
        const channels = state.chatChannels as Map<number, ChatMessage[]>;
        if (!channels.has(data.channel)) 
        {
            channels.set(data.channel, []);
            state.chatTracker = !state.chatTracker;
        }

        const channel = channels.get(data.channel);
        if (channel == null) 
        {
            throw "Failed to locate channel for chat message";
        }
        channel.push(data);
        state.chatTracker = !state.chatTracker;
    },
    [Mutations.deleteChatMessage](state: typeof typedState, data: ChatMessage): void 
    {
        const channels = state.chatChannels as Map<number, ChatMessage[]>;
        for (const channel of channels) 
        {
            const messages = channel[1];
            for (let i = 0; i < messages.length; ++i) 
            {
                if (messages[i].messageid == data.messageid) 
                {
                    messages.splice(i, 1);
                    state.chatTracker = !state.chatTracker;
                    return;
                }
            }
        }
    },
    [Mutations.resetProjectToDefault](state: typeof typedState): void 
    {
        Object.assign(state, getDefaultState());
    },
    [Mutations.timelineSyncNodesPostProcess](state: typeof typedState, data: { relatives: Map<number, any[]>, nodetags: Map<number, number[]> }): void 
    {
        console.log("Node FS done");
        for (const x of state.nodes.values()) 
        {
            const relativesForNode = data.relatives.get(x.nodeid);
            if (relativesForNode != undefined) 
            {
                console.log("Postprocessing node " + x.name + "(" + x.nodeid + ") with " + relativesForNode.length + " connections");
                for (const r of relativesForNode) 
                {
                    const rint = r.conn;
                    if (rint != undefined && state.nodes.has(rint)) 
                    {
                        const connection = new Connection();
                        connection.nodeid1 = Math.min(x.nodeid, rint);
                        connection.nodeid2 = Math.max(x.nodeid, rint);
                        if (r.text != undefined) 
                        {
                            const tup = StringPacking.Split(r.text);
                            connection.description = tup[0];
                            if (tup.length > 1) 
                            {
                                connection.references = tup[1];
                            }
                        }
                        if (r.direction != undefined) 
                        {
                            connection.direction = r.direction;
                        }
                        console.log("Postprocessing connection: " + connection.nodeid1 + "->" + connection.nodeid2
                            + " with description: " + connection.description + " and references: " + connection.references);
                        let m = state.connections.get(connection.nodeid1);
                        if (m != undefined) 
                        {
                            m.set(connection.nodeid2, connection);
                        }
                        else 
                        {
                            m = new Map<number, Connection>();
                            m.set(connection.nodeid2, connection);
                            state.connections.set(connection.nodeid1, m);
                        }
                        const node1 = state.nodes.get(connection.nodeid1);
                        const node2 = state.nodes.get(connection.nodeid2);
                        if (node1 != undefined && node2 != undefined) 
                        {
                            if (!node1.hasRelated(node2)) 
                            {
                                node1.addRelated(node2);
                            }
                            if (!node2.hasRelated(node1)) 
                            {
                                node2.addRelated(node1);
                            }
                        }
                    }
                    else 
                    {
                        console.log("ERRONEOUS NODE CONNECTION FOR " + x.nodeid + "->" + rint + " (node " + rint + " doesn't exist)");
                    }
                }
            }

            const nodetagsForNode = data.nodetags.get(x.nodeid);
            if (nodetagsForNode != undefined) 
            {
                for (const r of nodetagsForNode) 
                {
                    if (state.globalTags.has(r)) 
                    {
                        const t = state.globalTags.get(r);
                        if (t != undefined) 
                        {
                            x.tags.push(t);
                        }
                    }
                }
            }
        }
        state.connectionsTracker = !state.connectionsTracker;
        state.nodesTracker = !state.nodesTracker;
        console.log("Finished node postprocessing");
    },
    [Mutations.setNode](state: typeof typedState, data: Node) 
    {
        state.nodes.set(Number(data.nodeid), data);
        state.nodesTracker = !state.nodesTracker;
    },
    [Mutations.updateNodeColor](state: typeof typedState, data: any) 
    {
        const node = state.nodes.get(Number(data.nodeid));
        if (node != undefined) 
        {
            node.color = data.color;
            state.nodesTracker = !state.nodesTracker;
        }
    },
    [Mutations.setTag](state: typeof typedState, data: Tag) 
    {
        state.globalTags.set(Number(data.tagid), data);
        state.globalTagsTracker = !state.globalTagsTracker;
    },
    [Mutations.updateTagTitle](state: typeof typedState, data: any) 
    {
        const tag = state.globalTags.get(Number(data.tagid));
        if (tag != undefined) 
        {
            tag.name = data.name;
            state.globalTagsTracker = !state.globalTagsTracker;
        }
    },
    [Mutations.updateTagColor](state: typeof typedState, data: any) 
    {
        const tag = state.globalTags.get(Number(data.tagid));
        if (tag != undefined) 
        {
            tag.color = data.color;
            state.globalTagsTracker = !state.globalTagsTracker;
        }
    },
    [Mutations.setTimeline](state: typeof typedState, data: Timeline) 
    {
        state.timelines.set(data.id, data);
        state.timelinesTracker = !state.timelinesTracker;
    },
    [Mutations.updateMapData](state: typeof typedState, data: string) 
    {
        state.project.data = data;
    },
    [Mutations.setMapNode](state: typeof typedState, data: Node) 
    {
        state.mapNodes.set(data.nodeid, data);
        state.mapNodesTracker = !state.mapNodesTracker;
    },
    [Mutations.destroyMapNode](state: typeof typedState, data: number) 
    {
        state.mapNodes.delete(data);
        state.mapNodesTracker = !state.mapNodesTracker;
    },
    [Mutations.setProject](state: typeof typedState, data: Project) 
    {
        state.project = data;
    },
    [Mutations.setConnection](state: typeof typedState, data: Connection) 
    {
        const index1 = Math.min(data.nodeid1, data.nodeid2);
        const index2 = Math.max(data.nodeid1, data.nodeid2);

        const node1 = state.nodes.get(index1);
        const node2 = state.nodes.get(index2);
        if (node1 != undefined && node2 != undefined) 
        {
            if (!node1.hasRelated(node2)) 
            {
                node1.addRelated(node2);
            }
            if (!node2.hasRelated(node1)) 
            {
                node2.addRelated(node1);
            }
        }

        let m = state.connections.get(index1);
        if (m != undefined) 
        {
            let oldConn: Connection | undefined;
            if ((oldConn = m.get(index2))) 
            {
                // Hopefully this fixes reactivity
                oldConn.nodeid1 = data.nodeid1;
                oldConn.nodeid2 = data.nodeid2;
                oldConn.description = data.description;
                oldConn.references = data.references;
                oldConn.direction = data.direction;
            }
            else 
            {
                m.set(index2, data);
            }
        }
        else 
        {
            m = new Map<number, Connection>();
            m.set(index2, data);
            state.connections.set(index1, m);
        }

        state.connectionsTracker = !state.connectionsTracker;
        state.nodesTracker = !state.nodesTracker;
    },
    [Mutations.updateConnectionText](state: typeof typedState, data: Connection) 
    {
        let m = state.connections.get(data.nodeid1);
        if (m != undefined) 
        {
            let oldConn: Connection | undefined;
            if ((oldConn = m.get(data.nodeid2))) 
            {
                // Hopefully this fixes reactivity
                oldConn.nodeid1 = data.nodeid1;
                oldConn.nodeid2 = data.nodeid2;
                oldConn.description = data.description;
                oldConn.references = data.references;
                oldConn.direction = oldConn.direction;
            }
            else 
            {
                m.set(data.nodeid2, data);
            }
        }
        else 
        {
            m = new Map<number, Connection>();
            m.set(data.nodeid2, data);
            state.connections.set(data.nodeid1, m);
        }

        state.connectionsTracker = !state.connectionsTracker;
        state.nodesTracker = !state.nodesTracker;
    },
    [Mutations.updateConnectionDirection](state: typeof typedState, data: Connection) 
    {
        let m = state.connections.get(data.nodeid1);
        if (m != undefined) 
        {
            let oldConn: Connection | undefined;
            if ((oldConn = m.get(data.nodeid2))) 
            {
                // Hopefully this fixes reactivity
                oldConn.nodeid1 = data.nodeid1;
                oldConn.nodeid2 = data.nodeid2;
                oldConn.description = oldConn.description;
                oldConn.references = oldConn.references;
                oldConn.direction = data.direction;
            }
            else 
            {
                m.set(data.nodeid2, data);
            }
        }
        else 
        {
            m = new Map<number, Connection>();
            m.set(data.nodeid2, data);
            state.connections.set(data.nodeid1, m);
        }

        state.connectionsTracker = !state.connectionsTracker;
        state.nodesTracker = !state.nodesTracker;
    },
    [Mutations.removeConnection](state: typeof typedState, data: Connection) 
    {
        const index1 = Math.min(data.nodeid1, data.nodeid2);
        const index2 = Math.max(data.nodeid1, data.nodeid2);

        const m = state.connections.get(index1);
        if (m != undefined) 
        {
            if (m.delete(index2)) 
            {
                if (m.size == 0) 
                {
                    state.connections.delete(index1);
                }

                const node1 = state.nodes.get(index1);
                const node2 = state.nodes.get(index2);
                if (node1 != undefined && node2 != undefined) 
                {
                    node1.removeRelated(node2);
                    node2.removeRelated(node1);
                }

                state.connectionsTracker = !state.connectionsTracker;
                state.nodesTracker = !state.nodesTracker;
            }
            else 
            {
                alert(`Could not find delete connection ${index1}->${index2}`);
            }
        }
        else 
        {
            alert(`Could not find connection from I1: ${index1} (attempting to remove ${index1}->${index2})`);
        }
    },
    [Mutations.fullSyncMapNodesInit](state: typeof typedState) 
    {
        state.mapNodes = new Map<number, Node>();
    },
    [Mutations.fullSyncTimelinesInit](state: typeof typedState) 
    {
        state.timelines = new Map<number, Timeline>();
        state.mapToTimeline = new Map<number, number[]>();
    },
    [Mutations.setMapToTimeline](state: typeof typedState, data: { mapnodeid: number, timelineid: number }) 
    {
        // TODO: this implies an assumption that any timeline only has one mapnode; if that changes this code should change.
        for (const timelines of state.mapToTimeline.values()) 
        {
            if (timelines != null) 
            {
                timelines.remove(data.timelineid);
            }
        }

        let timelineList = state.mapToTimeline.get(data.mapnodeid);
        if (timelineList == undefined || timelineList == null) 
        {
            timelineList = [];
        }
        timelineList.push(data.timelineid);
        state.mapToTimeline.set(data.mapnodeid, timelineList);
        state.mapToTimelineTracker = !state.mapToTimelineTracker;
    },
    [Mutations.addTimelineNode](state: typeof typedState, data: { timelineid: number, nodeid: number }) 
    {
        console.log(data);
        const timeline = state.timelines.get(data.timelineid);
        if (timeline != undefined) 
        {
            if (timeline.nodeIDs == undefined || timeline.nodeIDs == null) 
            {
                timeline.nodeIDs = [];
            }
            if (!(timeline.nodeIDs.includes(data.nodeid))) 
            {
                timeline.nodeIDs.push(data.nodeid);
                const n = state.nodes.get(data.nodeid);
                if (n != null) 
                {

                    n.timelines.push(timeline);
                }

                // TODO: not sure this is the best spot for this
                const node = state.nodes.get(data.nodeid);
                if (node) 
                {
                    // timeline.patchEventOrdering(node);
                }

                // state.timelinesTracker = !(state.timelinesTracker); // inefficient
            }
        }
    },
    [Mutations.removeTimelineNode](state: typeof typedState, data: { timelineid: number, nodeid: number }) 
    {
        const timeline = state.timelines.get(data.timelineid);
        if (timeline != undefined) 
        {
            if (timeline.nodeIDs != null) 
            {
                timeline.nodeIDs.remove(data.nodeid);
                const n = state.nodes.get(data.nodeid);
                if (n != null) 
                {
                    n.timelines.remove(timeline);
                }
            }
        }

        // state.timelinesTracker = !(state.timelinesTracker); // inefficient
    },
    [Mutations.updateNodeTitle](state: typeof typedState, data: { nodeid: number, value: string }) 
    {
        const node = state.nodes.get(data.nodeid);
        if (node != undefined) 
        {
            node.name = data.value;
        }
        state.nodesTracker = !state.nodesTracker;
    },
    [Mutations.updateNodeDescription](state: typeof typedState, data: { nodeid: number, value: string }) 
    {
        const node = state.nodes.get(data.nodeid);
        if (node != undefined) 
        {
            Vue.set(node, 'description', data.value);
            //node.description = data.value;
        }
    },
    [Mutations.updateNodeReferences](state: typeof typedState, data: { nodeid: number, value: string }) 
    {
        const node = state.nodes.get(data.nodeid);
        if (node != undefined) 
        {
            Vue.set(node, 'references', data.value);
            //node.references = data.value;
        }
    },
    [Mutations.updateNodeImage](state: typeof typedState, data: { nodeid: number, value: string }) 
    {
        const node = state.nodes.get(data.nodeid);
        if (node != undefined) 
        {
            node.image = data.value;
        }
    },
    [Mutations.updateNodeVideo](state: typeof typedState, data: { nodeid: number, value: string }) 
    {
        const node = state.nodes.get(data.nodeid);
        if (node != undefined) 
        {
            node.video = data.value;
        }
    },
    [Mutations.updateNodeImageCaption](state: typeof typedState, data: { nodeid: number, value: string }) 
    {
        const node = state.nodes.get(data.nodeid);
        if (node != undefined) 
        {
            node.imageCaption = data.value;
        }
    },
    [Mutations.updateNodeAudio](state: typeof typedState, data: { nodeid: number, value: string }) 
    {
        const node = state.nodes.get(data.nodeid);
        if (node != undefined) 
        {
            node.audio = data.value;
        }
    },
    [Mutations.updateNodeImportant](state: typeof typedState, data: { nodeid: number, value: boolean }) 
    {
        const node = state.nodes.get(data.nodeid);
        if (node != undefined) 
        {
            node.important = data.value;
        }
    },
    [Mutations.updateNodeDateOptions](state: typeof typedState, data: { nodeid: number, value: DateOptions }) 
    {
        const node = state.nodes.get(data.nodeid);
        if (node != undefined) 
        {
            node.dateOptions = new DateOptions(data.value);
        }
    },
    [Mutations.updateNodeStart](state: typeof typedState, data: { nodeid: number, value: Date }) 
    {
        console.log('updating node start');
        const node = state.nodes.get(data.nodeid);
        if (node != undefined) 
        {
            node.updateDateStart(data.value); // handles re-sorting related nodes structures (which get used by the layout algorithm)
            if (node.component && node.component.forceUpdateDates) 
            {
                node.component.forceUpdateDates();
            }
        }
    },
    [Mutations.updateNodeEnd](state: typeof typedState, data: { nodeid: number, value: Date }) 
    {
        const node = state.nodes.get(data.nodeid);
        if (node != undefined) 
        {
            Vue.set(node, 'dateEnd', data.value);
            if (node.component && node.component.forceUpdateDates) 
            {
                node.component.forceUpdateDates();
            }
        }
    },
    [Mutations.addNodeTag](state: typeof typedState, data: { nodeid: number, tagid: number }) 
    {
        const node = state.nodes.get(data.nodeid);
        if (node != undefined) 
        {
            const tag = state.globalTags.get(data.tagid);
            if (tag != undefined) 
            {
                if (node.tags == null) 
                {
                    Vue.set(node, 'tags', []);
                }
                console.log(`Adding tag ${tag.name} to node ${node.name}`);
                node.tags.push(tag);
                node.timelines.forEach((tl) => 
                {
                    if (!(tl.tags.some((t) => 
                    {
                        return t.tagid === data.tagid; 
                    }))) 
                    {
                        SendPacket(PacketType.TimelineAddTag,
                            {
                                tagid: data.tagid,
                                timelineid: tl.id
                            });
                    }
                });
                if (node.component && node.component.forceUpdateTags) 
                {
                    node.component.forceUpdateTags();
                }
            }
        }
    },
    [Mutations.removeNodeTag](state: typeof typedState, data: { nodeid: number, tagid: number }) 
    {
        const node = state.nodes.get(data.nodeid);
        if (node != undefined) 
        {
            const tag = state.globalTags.get(data.tagid);
            if (tag != undefined) 
            {
                if (node.tags == null) 
                {
                    Vue.set(node, 'tags', []);
                }
                node.tags.remove(tag);
                console.log(`Removed tag ${tag.name} to node ${node.name}`);
                if (node.component && node.component.forceUpdateTags) 
                {
                    node.component.forceUpdateTags();
                }
            }
        }
    },
    [Mutations.addTimelineTag](state: typeof typedState, data: { timelineid: number, tagid: number }) 
    {
        const tl = state.timelines.get(data.timelineid);
        if (tl != undefined) 
        {
            const tag = state.globalTags.get(data.tagid);
            if (tag != undefined) 
            {
                if (tl.tags == null) 
                {
                    Vue.set(tl, 'tags', []);
                }
                tl.tags.push(tag);
                tag.timelineCount += 1;
            }
        }
    },
    [Mutations.removeTimelineTag](state: typeof typedState, data: { timelineid: number, tagid: number }) 
    {
        const tl = state.timelines.get(data.timelineid);
        if (tl != undefined) 
        {
            const tag = state.globalTags.get(data.tagid);
            if (tag != undefined) 
            {
                tl.tags.remove(tag);
                tag.timelineCount -= 1;
            }
        }
    },
    [Mutations.deleteNode](state: typeof typedState, data: { nodeid: number }) 
    {
        if (state.nodes.delete(data.nodeid)) 
        {
            state.nodesTracker = !state.nodesTracker;
            for (const tl of state.timelines.values()) 
            {
                if (tl.nodeIDs != null) 
                {
                    if (tl.nodeIDs.remove(data.nodeid)) 
                    {
                        state.timelinesTracker = !state.timelinesTracker;
                        for (const kvp of tl.eventOrderings.entries()) 
                        {
                            kvp[1].remove(data.nodeid);
                            tl.setEventOrdering(kvp[1], kvp[0]);
                        }
                    }
                }
            }
            let changed: boolean = state.connections.delete(data.nodeid);
            for (const conns of state.connections.values()) 
            {
                if (conns.delete(data.nodeid)) 
                {
                    changed = true;
                }
            }
            if (changed) 
            {
                state.connectionsTracker = !state.connectionsTracker;
            }
        }
    },
    [Mutations.updateTimelineTitle](state: typeof typedState, data: { timelineid: number, value: string }) 
    {
        const timeline = state.timelines.get(data.timelineid);
        if (timeline != undefined) 
        {
            timeline.name = data.value;
        }
    },
    [Mutations.updateTimelineDescription](state: typeof typedState, data: { timelineid: number, value: string }) 
    {
        const timeline = state.timelines.get(data.timelineid);
        if (timeline != undefined) 
        {
            timeline.description = data.value;
        }
    },
    [Mutations.updateTimelineComments](state: typeof typedState, data: { timelineid: number, value: string }) 
    {
        const timeline = state.timelines.get(data.timelineid);
        if (timeline != undefined) 
        {
            timeline.comments = data.value;
        }
    },
    [Mutations.setTimelineAccess](state: typeof typedState, data: { timelineid: number, access: TimelineAccess }) 
    {
        const timeline = state.timelines.get(data.timelineid);
        if (timeline != undefined) 
        {
            if (timeline.accessList == null) 
            {
                timeline.accessList = new Map<string, TimelineAccess>();
            }
            timeline.accessList.set(data.access.User.userid.toLowerCase(), TimelineAccess.fromJsonObject(data.access));
        }
    },
    [Mutations.timelineSyncTimelineTags](state: typeof typedState, data: { timelineid: number, timelinetags: number[] }) 
    {
        const timeline = state.timelines.get(data.timelineid);
        if (timeline != undefined) 
        {
            if (timeline.tags == null) 
            {
                timeline.tags = [];
            }
            else 
            {
                timeline.tags.length = 0;
            }
            for (const tagid of data.timelinetags) 
            {
                const tag = state.globalTags.get(tagid);
                if (tag) 
                {
                    timeline.tags.push(tag);
                    tag.timelineCount += 1;
                }
                else 
                {
                    alert(`Error when syncing timeline: Invalid tag in timeline {}`);
                    window.location.reload();
                    return;
                }
            }
        }
        else 
        {
            alert(`Error when syncing timeline: No timeline found for ID ${data.timelineid}`);
        }
    },
    [Mutations.setTimelineAccessList](state: typeof typedState, data: { timelineid: number, accesslist: TimelineAccess[] }) 
    {
        const timeline = state.timelines.get(data.timelineid);
        if (timeline != undefined) 
        {
            if (timeline.accessList == null) 
            {
                timeline.accessList = new Map<string, TimelineAccess>();
            }
            timeline.accessList.clear();
            for (const ta of data.accesslist) 
            {
                const converted = TimelineAccess.fromJsonObject(ta);
                timeline.accessList.set(ta.User.userid.toLowerCase(), converted);
            }
        }
    },
    [Mutations.setTimelineEventOrdering](state: typeof typedState, data: { timelineid: number, name: string, ordering: number[] }) 
    {
        const timeline = state.timelines.get(data.timelineid);
        if (timeline != undefined) 
        {
            if (timeline.eventOrderings == null) 
            {
                timeline.eventOrderings = new Map<string, number[]>();
            }
            if (data.ordering == null) 
            {
                timeline.eventOrderings.delete(data.name);
            }
            else 
            {
                timeline.eventOrderings.set(data.name, data.ordering);
            }
        }
    },
    [Mutations.deleteTimeline](state: typeof typedState, data: { timelineid: number }) 
    {
        const tl = state.timelines.get(data.timelineid);
        if (tl != undefined) 
        {
            for (const m of state.mapToTimeline.values()) 
            {
                if (m != null) 
                {
                    if (m.remove(tl.id)) 
                    {
                        state.mapToTimelineTracker = !state.mapToTimelineTracker;
                    }
                }
            }
            for (const nID of tl.nodeIDs) 
            {
                const n = state.nodes.get(nID);
                if (n != null) 
                {
                    n.timelines.remove(tl);
                }
            }
            for (const t of tl.tags) 
            {
                t.timelineCount -= 1;
            }
            state.timelines.delete(data.timelineid);
            state.timelinesTracker = !state.timelinesTracker;
        }
    },
    [Mutations.deleteTag](state: typeof typedState, data: { tagid: number }) 
    {
        const tag = state.globalTags.get(data.tagid);
        if (tag != undefined) 
        {
            for (const tl of state.timelines.values()) 
            {
                if (tl.tags != null) 
                {
                    tl.tags.remove(tag);
                }
            }
            for (const node of state.nodes.values()) 
            {
                if (node.tags != null) 
                {
                    node.tags.remove(tag);
                }
            }
            state.globalTags.delete(data.tagid);
            state.globalTagsTracker = !state.globalTagsTracker;
        }
    },
    [Mutations.setActiveTimeline](state: typeof typedState, data: { activeTimeline: Timeline }) 
    {
        state.activeTimeline = data.activeTimeline;
        state.selected = null;
        state.presentationMode = false;
        state.presentationFullSlide = -1;
    },
    [Mutations.flagCopyingTimeline](state: typeof typedState, data: { timelineid: number }) 
    {
        state.copyingTimelines.push(data.timelineid);
    },
    [Mutations.clearCopyingTimeline](state: typeof typedState, data: { timelineid: number }) 
    {
        state.copyingTimelines.remove(data.timelineid);
    },
    [Mutations.setSelected](state: typeof typedState, data: any) 
    {
        state.lastSelected = state.selected;
        state.selected = state.selected === data ? null : data;
    },
    [Mutations.setLocalUserData](state: typeof typedState, data: any) 
    {
        state.project.localPermissions = data.permissions;
    },
    [Mutations.setAccessedTimelines](state: typeof typedState, data: any) 
    {
        state.accessedTimelines = data;
    },
    [Mutations.setTimelineSyncing](state: typeof typedState, data: any) 
    {
        state.timelineSyncing = data;
    },
    [Mutations.setPresentationMode](state: typeof typedState, data: any) 
    {
        state.presentationMode = data;
        state.presentationFullSlide = -1;
    },
    [Mutations.setPresentationFullSlide](state: typeof typedState, data: any) 
    {
        state.presentationFullSlide = data;
        if (state.presentationFullSlide >= 0 && state.nodes.has(state.presentationFullSlide)) 
        {
            store.commit(Mutations.setSelected, state.nodes.get(state.presentationFullSlide));
        }
    },
    [Mutations.setTimelineFormMode](state: typeof typedState, data: any) 
    {
        state.timelineFormMode = data;
    },
    [Mutations.updateProjectMetadata](state: typeof typedState, data: any) 
    {
        state.project.name = data.projectname;
        state.project.description = data.projectdescription;
        state.project.groups = data.usergroups;
        for (let i = 0; i < state.project.groups.length; ++i) 
        {
            if (state.project.groups[i].id == data.groupid) 
            {
                state.project.group = state.project.groups[i];
                break;
            }
        }
    },
    [Mutations.setFolder](state: typeof typedState, data: SetFolderData) 
    {
        const fold = new Folder;
        fold.id = data.id;
        fold.name = data.name;
        fold.description = data.description;
        for(const access of data.accessList)
        {
            fold.accessList.push(TimelineAccess.fromJsonObject(access));
        }
        state.folders.set(fold.id, fold);
        state.folderTracker = !state.folderTracker;
    },
    [Mutations.deleteFolder](state: typeof typedState, data: DeleteFolderData) 
    {
        if (state.folders.has(data.id)) 
        {
            state.folders.delete(data.id);
            state.folderTracker = !state.folderTracker;
        }
    },
    [Mutations.setTimelineFolder](state: typeof typedState, data: TimelineSetFolderData) 
    {
        console.log('setTimelineFolder');
        if (state.timelines.has(data.timelineid)) 
        {
            const tl = state.timelines.get(data.timelineid);
            if (!tl) 
            {
                console.error('Failed to set timeline folder - couldn\'t find timeline AFTER checking for `has`');
                return;
            }

            console.log(`setting timeline ${tl.name} to ${data.folderid}`);
            tl.folderid = data.folderid;
        }
        else 
        {
            console.error('Failed to set timeline folder - couldn\'t find timeline');
        }
        state.folderTracker = !state.folderTracker;
        state.timelinesTracker = !state.timelinesTracker;
    },
    [Mutations.setFolderAccess](state: typeof typedState, data: FolderUpdateAccessData)
    {
        console.log('setFolderAccess');
        const folder = state.folders.get(data.folderid);
        if (folder != undefined)
        {
            console.log(`setting folder ${folder.name} access for ${data.access.User.userid} to ${data.access.Permission}`);
            const curIdx = folder.accessList.findIndex((x) => x.User.userid === data.access.User.userid);
            if (curIdx >= 0)
            {
                console.log('updating existing access');
                folder.accessList[curIdx] = TimelineAccess.fromJsonObject(data.access);
            }
            else
            {
                console.log('adding new access');
                folder.accessList.push(TimelineAccess.fromJsonObject(data.access));
            }
        }
    },
    // Functionally empty mutation that serves simply as a notification
    [Mutations.notifyTagFilterUpdated]()
    {}
};

const actions =
{
    async getProjectSubscription(context: { commit: any, state: typeof typedState }, projectid: number | undefined) 
    {
        let tier = 0;

        try 
        {
            const pid = ((projectid != undefined) ? projectid : state.project.id);
            const resp = await Vue.axios.get<{ tier: number }>(`payments/subscription/tier/${pid}`);
            tier = resp.data.tier;
        }
        catch (err) 
        {
            console.error(err); // generally gonna be harmless 404s
        }

        if (projectid == undefined) 
        {
            state.project.subscriptionTier = tier; // TODO: does this need a mutation?
        }
        return tier;
    },
    async getProjectTiers(context: { commit: any, state: typeof typedState }) 
    {
        let tiers = null;

        try 
        {
            const resp = await Vue.axios.get<{ tiers: Map<string, number> }>(`payments/subscription/tiers`);
            tiers = resp.data.tiers;
        }
        catch (err) 
        {
            console.error(err); // generally gonna be harmless 404s
        }

        console.log(tiers); // TODO: remove once this is tested
        return tiers;
    }
};

export default
{
    state,
    getters,
    actions,
    mutations
};